import { router } from "@inertiajs/react"
import { msg } from "@lingui/core/macro"
import type { MessageDescriptor, I18n } from "@lingui/core"

// The command-palette "command mode" registry (CON-184).
//
// Typing `>` in the palette switches from search to this list of
// runnable commands. The SAME registry feeds the cold-start
// "Or run a command" chips, so the two can never drift.
//
// Scope note: the Paper command-mode artboard also draws Create
// contact / Create company and Add note / Schedule next step.
// Those flows don't exist in v2 yet (no create route for
// contacts/companies; note + next-step need a record picker that
// isn't built) and their forms aren't designed, so they are
// deliberately omitted here and tracked as follow-ups rather
// than shipped as dead affordances.

export type CommandSection = "create" | "navigate"

export interface PaletteCommand {
  // Stable id (used as the React key + test id).
  id: string
  label: MessageDescriptor
  section: CommandSection
  // Right-aligned hint shown on the row (e.g. "Opens form").
  hint?: MessageDescriptor
  // Extra words matched by the `>` filter beyond the label.
  keywords?: string[]
  // Performs the action. The palette closes itself after
  // calling this — `run` only navigates / opens.
  run: () => void
}

// "Create deal" reuses the existing deals-index CreateDealModal
// rather than duplicating its pipeline/owner wiring globally: we
// navigate to the deals board with a one-shot `new_deal` token,
// which the page reads to auto-open the modal. The token is a
// timestamp so re-running the command always yields a fresh URL
// the page reacts to (Inertia skips a visit to the identical
// URL). Keeps deal-create logic in one place.
export const NEW_DEAL_PARAM = "new_deal"

export function newDealUrl(): string {
  return `/v2/deals?${NEW_DEAL_PARAM}=${Date.now()}`
}

export const PALETTE_COMMANDS: readonly PaletteCommand[] = [
  {
    id: "create-deal",
    label: msg`Create deal`,
    section: "create",
    hint: msg`Opens form`,
    keywords: ["new", "deal", "add"],
    run: () => router.visit(newDealUrl()),
  },
  {
    id: "go-inbox",
    label: msg`Go to Inbox`,
    section: "navigate",
    keywords: ["inbox", "conversations", "messages"],
    run: () => router.visit("/v2/inbox"),
  },
  {
    id: "go-today",
    label: msg`Go to Today`,
    section: "navigate",
    keywords: ["today", "agenda", "tasks"],
    run: () => router.visit("/v2/today"),
  },
  {
    id: "go-deals",
    label: msg`Go to Deals`,
    section: "navigate",
    keywords: ["deals", "pipeline", "board"],
    run: () => router.visit("/v2/deals"),
  },
  {
    id: "go-settings-channels",
    label: msg`Go to Settings › Channels`,
    section: "navigate",
    keywords: ["settings", "channels", "whatsapp", "connect"],
    run: () => router.visit("/v2/settings/channels"),
  },
  {
    id: "go-settings-templates",
    label: msg`Go to Settings › Templates`,
    section: "navigate",
    keywords: ["settings", "templates", "whatsapp", "messages"],
    // Server-side redirect — the templates page is nested under
    // a specific WhatsApp connection id the palette can't know.
    run: () => router.visit("/v2/settings/templates"),
  },
] as const

// The ordered sections rendered in command mode (CREATE first,
// then NAVIGATE), matching the Paper artboard.
export const COMMAND_SECTIONS: readonly {
  section: CommandSection
  label: MessageDescriptor
}[] = [
  { section: "create", label: msg`Create` },
  { section: "navigate", label: msg`Navigate` },
] as const

// The subset surfaced as cold-start "Or run a command" chips on
// first run. A small, high-intent teaser of the full list (the
// `>` mode shows everything).
export const COLD_START_COMMAND_IDS: readonly string[] = [
  "create-deal",
  "go-today",
  "go-deals",
] as const

// Filter the registry by the text typed after `>`. Empty filter
// returns everything (in registry order). Matches the translated
// label and any keyword, case-insensitively.
export function filterCommands(filter: string, i18n: I18n): PaletteCommand[] {
  const needle = filter.trim().toLowerCase()
  if (needle === "") return [...PALETTE_COMMANDS]
  return PALETTE_COMMANDS.filter((command) => {
    if (i18n._(command.label).toLowerCase().includes(needle)) return true
    return (command.keywords ?? []).some((keyword) =>
      keyword.toLowerCase().includes(needle),
    )
  })
}

export function coldStartCommands(): PaletteCommand[] {
  return COLD_START_COMMAND_IDS.map((id) =>
    PALETTE_COMMANDS.find((command) => command.id === id),
  ).filter((command): command is PaletteCommand => command != null)
}
